import React, { useState, useEffect } from 'react'
import { WorkOrder, WorkOrderStatus, WorkOrderType, DeviceFault, WorkOrderPart, WorkOrderActivity, BillOfMaterials, ComponentGrade, DeviceManagementDevice } from '../types'
import { WorkOrdersService, DevicesService } from '../services/api'
import { v4 as uuidv4 } from 'uuid'

const WorkOrders: React.FC = () => {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<DeviceManagementDevice[]>([])
  const [deviceLookupError, setDeviceLookupError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [editingWorkOrder, setEditingWorkOrder] = useState<WorkOrder | null>(null)
  const [formData, setFormData] = useState<Partial<WorkOrder>>({
    deviceSerialNumber: '',
    deviceCategory: 'Scanner Pro V2',
    workOrderType: WorkOrderType.REFURBISHMENT,
    location: '',
    operator: 'demo-user',
    faults: [],
    partsUsed: [],
    activities: [],
    targetBom: '',
    status: WorkOrderStatus.CREATED,
    notes: ''
  })

  const [availableFaults] = useState<DeviceFault[]>([
    { id: '1', description: 'Battery not charging', deviceCategory: 'Scanner Pro V2', isActive: true },
    { id: '2', description: 'Display flickering', deviceCategory: 'Scanner Pro V2', isActive: true },
    { id: '3', description: 'Wand not responding', deviceCategory: 'Scanner Pro V2', isActive: true },
    { id: '4', description: 'Housing cracked', deviceCategory: 'Scanner Pro V2', isActive: true },
    { id: '5', description: 'Sensor malfunction', deviceCategory: 'Scanner Pro V2', isActive: true }
  ])

  const [parts] = useState([
    { id: '1', name: 'Battery Pack', sku: 'BAT-001' },
    { id: '2', name: 'Display Screen', sku: 'DSP-001' },
    { id: '3', name: 'Main Wand', sku: 'WND-001' },
    { id: '4', name: 'Housing Case', sku: 'HSG-001' }
  ])

  // Sample BOMs for kitting work orders
  const [availableBOMs] = useState<BillOfMaterials[]>([
    {
      id: 'bom-1',
      name: 'Scanner Pro V2 Full Kit',
      deviceSku: 'Scanner Pro V2',
      version: '1.0',
      parts: [
        { partId: '1', quantity: 1, allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A, ComponentGrade.GRADE_B], isRequired: true },
        { partId: '2', quantity: 1, allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A], isRequired: true },
        { partId: '3', quantity: 1, allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A, ComponentGrade.GRADE_B], isRequired: true }
      ],
      createdDate: '2024-01-01',
      createdBy: 'admin',
      isActive: true
    },
    {
      id: 'bom-2',
      name: 'Scanner Pro V2 Basic Kit',
      deviceSku: 'Scanner Pro V2',
      version: '1.0',
      parts: [
        { partId: '1', quantity: 1, allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A, ComponentGrade.GRADE_B], isRequired: true },
        { partId: '3', quantity: 1, allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A, ComponentGrade.GRADE_B], isRequired: true }
      ],
      createdDate: '2024-01-01',
      createdBy: 'admin',
      isActive: true
    }
  ])

  // Load work orders on component mount
  useEffect(() => {
    loadWorkOrders()
    loadDevices()
  }, [])

  const loadWorkOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await WorkOrdersService.getAllWorkOrders()
      setWorkOrders(data)
    } catch (error) {
      console.error('Failed to load work orders:', error)
      setError('Failed to load work orders. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadDevices = async () => {
    try {
      setDeviceLookupError(null)
      const data = await DevicesService.getAllDevices()
      setDevices(data)
    } catch (error) {
      console.error('Failed to load devices:', error)
      setDeviceLookupError('Failed to load device list for lookup.')
    }
  }

  const handleDeviceSerialNumberChange = async (serialNumber: string) => {
    setFormData(prev => ({ ...prev, deviceSerialNumber: serialNumber }))
    
    if (serialNumber.length > 2) {
      try {
        const device = await DevicesService.getDeviceBySerialNumber(serialNumber)
        if (device) {
          setFormData(prev => ({
            ...prev,
            deviceCategory: device.sku || prev.deviceCategory,
            deviceId: device.id
          }))
          setDeviceLookupError(null)
        } else if (serialNumber.length > 5) {
          setDeviceLookupError(`No device found with serial number: ${serialNumber}`)
        }
      } catch (error) {
        console.error('Error looking up device:', error)
      }
    } else {
      setDeviceLookupError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      setError(null)

      if (editingWorkOrder) {
        // Update existing work order
        const updatedWorkOrder = await WorkOrdersService.updateWorkOrder(editingWorkOrder.id, formData)
        setWorkOrders(prev => prev.map(wo => 
          wo.id === editingWorkOrder.id ? updatedWorkOrder : wo
        ))
      } else {
        // Create new work order
        const workOrderData = {
          deviceId: formData.deviceId || uuidv4(), // Use looked up device ID or generate one
          deviceSerialNumber: formData.deviceSerialNumber!,
          deviceCategory: formData.deviceCategory!,
          workOrderType: formData.workOrderType!,
          location: formData.location!,
          operator: formData.operator!,
          faults: formData.faults || [],
          partsUsed: formData.partsUsed || [],
          activities: formData.activities || [],
          targetBom: formData.targetBom,
          startTime: new Date().toISOString(),
          status: formData.status || WorkOrderStatus.CREATED,
          notes: formData.notes
        }
        
        const newWorkOrder = await WorkOrdersService.createWorkOrder(workOrderData)
        setWorkOrders(prev => [...prev, newWorkOrder])
      }

      resetForm()
    } catch (error) {
      console.error('Failed to save work order:', error)
      setError('Failed to save work order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      deviceSerialNumber: '',
      deviceCategory: 'Scanner Pro V2',
      workOrderType: WorkOrderType.REFURBISHMENT,
      location: '',
      operator: 'demo-user',
      faults: [],
      partsUsed: [],
      activities: [],
      targetBom: '',
      status: WorkOrderStatus.CREATED,
      notes: ''
    })
    setShowForm(false)
    setEditingWorkOrder(null)
    setDeviceLookupError(null)
  }

  const handleEdit = (workOrder: WorkOrder) => {
    setEditingWorkOrder(workOrder)
    setFormData(workOrder)
    setShowForm(true)
  }

  const handleStatusChange = async (workOrderId: string, newStatus: WorkOrderStatus) => {
    try {
      setError(null)
      const updateData = {
        status: newStatus,
        endTime: newStatus === WorkOrderStatus.COMPLETED ? new Date().toISOString() : undefined
      }
      
      const updatedWorkOrder = await WorkOrdersService.updateWorkOrder(workOrderId, updateData)
      setWorkOrders(prev => prev.map(wo => 
        wo.id === workOrderId ? updatedWorkOrder : wo
      ))
    } catch (error) {
      console.error('Failed to update work order status:', error)
      setError('Failed to update work order status. Please try again.')
    }
  }

  const handleDelete = async (workOrderId: string) => {
    if (!window.confirm('Are you sure you want to delete this work order?')) {
      return
    }

    try {
      setError(null)
      await WorkOrdersService.deleteWorkOrder(workOrderId)
      setWorkOrders(prev => prev.filter(wo => wo.id !== workOrderId))
    } catch (error) {
      console.error('Failed to delete work order:', error)
      setError('Failed to delete work order. Please try again.')
    }
  }

  const addFaultToWorkOrder = (faultId: string) => {
    const fault = availableFaults.find(f => f.id === faultId)
    if (fault && !formData.faults?.some(f => f.id === faultId)) {
      setFormData(prev => ({
        ...prev,
        faults: [...(prev.faults || []), fault]
      }))
    }
  }

  const removeFaultFromWorkOrder = (faultId: string) => {
    setFormData(prev => ({
      ...prev,
      faults: prev.faults?.filter(f => f.id !== faultId) || []
    }))
  }

  const addPartToWorkOrder = () => {
    const newPart: WorkOrderPart = {
      partId: parts[0]?.id || '',
      quantity: 1,
      timeUsedMinutes: 0
    }
    setFormData(prev => ({
      ...prev,
      partsUsed: [...(prev.partsUsed || []), newPart]
    }))
  }

  const updateWorkOrderPart = (index: number, updatedPart: Partial<WorkOrderPart>) => {
    setFormData(prev => ({
      ...prev,
      partsUsed: prev.partsUsed?.map((part, i) => i === index ? { ...part, ...updatedPart } : part) || []
    }))
  }

  const removeWorkOrderPart = (index: number) => {
    setFormData(prev => ({
      ...prev,
      partsUsed: prev.partsUsed?.filter((_, i) => i !== index) || []
    }))
  }

  // Default activities for work order types
  const getDefaultActivities = (workOrderType: WorkOrderType): WorkOrderActivity[] => {
    const defaultActivitiesMap: Record<WorkOrderType, WorkOrderActivity[]> = {
      [WorkOrderType.RECYCLING]: [
        {
          id: 'recycle-1',
          description: 'Remove all personal data',
          isCompleted: false,
          estimatedMinutes: 15,
          notes: ''
        },
        {
          id: 'recycle-2',
          description: 'Disassemble device components',
          isCompleted: false,
          estimatedMinutes: 30,
          notes: ''
        },
        {
          id: 'recycle-3',
          description: 'Sort components by material type',
          isCompleted: false,
          estimatedMinutes: 20,
          notes: ''
        },
        {
          id: 'recycle-4',
          description: 'Package for recycling disposal',
          isCompleted: false,
          estimatedMinutes: 10,
          notes: ''
        }
      ],
      [WorkOrderType.KITTING]: [
        {
          id: 'kit-1',
          description: 'Verify all required parts are available',
          isCompleted: false,
          estimatedMinutes: 10,
          notes: ''
        },
        {
          id: 'kit-2',
          description: 'Check part quality and grades',
          isCompleted: false,
          estimatedMinutes: 15,
          notes: ''
        },
        {
          id: 'kit-3',
          description: 'Package parts according to BOM',
          isCompleted: false,
          estimatedMinutes: 20,
          notes: ''
        },
        {
          id: 'kit-4',
          description: 'Label kit with device information',
          isCompleted: false,
          estimatedMinutes: 5,
          notes: ''
        }
      ],
      [WorkOrderType.REFURBISHMENT]: []
    }

    return defaultActivitiesMap[workOrderType] || []
  }

  // Handle work order type change and populate default activities
  const handleWorkOrderTypeChange = (workOrderType: WorkOrderType) => {
    const defaultActivities = getDefaultActivities(workOrderType)
    
    setFormData(prev => ({
      ...prev,
      workOrderType,
      activities: defaultActivities,
      // Reset BOM selection if switching away from kitting
      ...(workOrderType !== WorkOrderType.KITTING ? { targetBom: '' } : {})
    }))
  }

  // Add parts from BOM to work order when BOM is selected
  const handleBomSelection = (bomId: string) => {
    const selectedBom = availableBOMs.find(bom => bom.id === bomId)
    if (selectedBom) {
      const bomParts: WorkOrderPart[] = selectedBom.parts.map(bomPart => ({
        partId: bomPart.partId,
        quantity: bomPart.quantity,
        timeUsedMinutes: 0
      }))

      setFormData(prev => ({
        ...prev,
        targetBom: bomId,
        partsUsed: bomParts
      }))
    }
  }

  // Activity management functions
  const addActivity = () => {
    const newActivity: WorkOrderActivity = {
      id: `activity-${Date.now()}`,
      description: '',
      isCompleted: false,
      estimatedMinutes: 30,
      notes: ''
    }
    setFormData(prev => ({
      ...prev,
      activities: [...(prev.activities || []), newActivity]
    }))
  }

  const updateActivity = (index: number, updatedActivity: Partial<WorkOrderActivity>) => {
    setFormData(prev => ({
      ...prev,
      activities: prev.activities?.map((activity, i) => 
        i === index ? { ...activity, ...updatedActivity } : activity
      ) || []
    }))
  }

  const removeActivity = (index: number) => {
    setFormData(prev => ({
      ...prev,
      activities: prev.activities?.filter((_, i) => i !== index) || []
    }))
  }

  const getStatusColor = (status: WorkOrderStatus) => {
    switch (status) {
      case WorkOrderStatus.CREATED:
        return 'status-new'
      case WorkOrderStatus.IN_PROGRESS:
        return 'status-grade-b'
      case WorkOrderStatus.AWAITING_PARTS:
        return 'status-grade-c'
      case WorkOrderStatus.QUALITY_INSPECTION:
        return 'status-grade-a'
      case WorkOrderStatus.COMPLETED:
        return 'status-grade-a'
      case WorkOrderStatus.CANCELLED:
        return 'status-scrap'
      default:
        return 'status-new'
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Work Orders</h2>
        <button 
          onClick={() => setShowForm(true)} 
          className="btn btn-primary"
          disabled={showForm || loading}
        >
          Create Work Order
        </button>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '4px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#c00'
        }}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ float: 'right', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#666' 
        }}>
          Loading work orders...
        </div>
      )}

      {showForm && (
        <div className="form-section" style={{ marginBottom: '2rem' }}>
          <h3>{editingWorkOrder ? 'Edit Work Order' : 'Create New Work Order'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="deviceSerialNumber">Device Serial Number *</label>
                <input
                  type="text"
                  id="deviceSerialNumber"
                  className="form-control"
                  value={formData.deviceSerialNumber || ''}
                  onChange={(e) => handleDeviceSerialNumberChange(e.target.value)}
                  list="device-serials"
                  required
                />
                <datalist id="device-serials">
                  {devices
                    .filter(device => 
                      formData.deviceSerialNumber && 
                      device.serialNumber.toLowerCase().includes(formData.deviceSerialNumber.toLowerCase())
                    )
                    .slice(0, 5)
                    .map(device => (
                      <option key={device.id} value={device.serialNumber}>
                        {device.serialNumber} ({device.sku})
                      </option>
                    ))
                  }
                </datalist>
                {deviceLookupError && (
                  <div style={{ color: '#f44336', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    {deviceLookupError}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="workOrderType">Work Order Type *</label>
                <select
                  id="workOrderType"
                  className="form-control"
                  value={formData.workOrderType || WorkOrderType.REFURBISHMENT}
                  onChange={(e) => handleWorkOrderTypeChange(e.target.value as WorkOrderType)}
                  required
                >
                  <option value={WorkOrderType.REFURBISHMENT}>Refurbishment</option>
                  <option value={WorkOrderType.RECYCLING}>Recycling</option>
                  <option value={WorkOrderType.KITTING}>Kitting</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <select
                  id="location"
                  className="form-control"
                  value={formData.location || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  required
                >
                  <option value="">Select Location</option>
                  <option value="Workshop A">Workshop A</option>
                  <option value="Workshop B">Workshop B</option>
                  <option value="Workshop C">Workshop C</option>
                  <option value="Field Service">Field Service</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', marginTop: '1rem' }}>
              <div className="form-group">
                <label htmlFor="operator">Operator *</label>
                <input
                  type="text"
                  id="operator"
                  className="form-control"
                  value={formData.operator || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, operator: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* BOM Selection for Kitting Work Orders */}
            {formData.workOrderType === WorkOrderType.KITTING && (
              <div style={{ marginTop: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="targetBom">Target BOM</label>
                  <select
                    id="targetBom"
                    className="form-control"
                    value={formData.targetBom || ''}
                    onChange={(e) => handleBomSelection(e.target.value)}
                  >
                    <option value="">Select a BOM...</option>
                    {availableBOMs.map(bom => (
                      <option key={bom.id} value={bom.id}>
                        {bom.name} (v{bom.version})
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Selecting a BOM will automatically add the required parts to this work order.
                  </small>
                </div>
              </div>
            )}

            <div style={{ marginTop: '2rem' }}>
              <h4>Device Faults</h4>
              <div style={{ marginBottom: '1rem' }}>
                <select
                  className="form-control"
                  onChange={(e) => addFaultToWorkOrder(e.target.value)}
                  value=""
                >
                  <option value="">Add a fault...</option>
                  {availableFaults
                    .filter(fault => !formData.faults?.some(f => f.id === fault.id))
                    .map(fault => (
                      <option key={fault.id} value={fault.id}>
                        {fault.description}
                      </option>
                    ))
                  }
                </select>
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {formData.faults?.map(fault => (
                  <div key={fault.id} style={{ 
                    background: '#f0f0f0', 
                    padding: '0.5rem 1rem', 
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {fault.description}
                    <button 
                      type="button" 
                      onClick={() => removeFaultFromWorkOrder(fault.id)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#f44336', 
                        cursor: 'pointer' 
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>Parts Used ({formData.partsUsed?.length || 0})</h4>
                <button type="button" onClick={addPartToWorkOrder} className="btn btn-secondary">
                  Add Part
                </button>
              </div>

              {formData.partsUsed?.map((workOrderPart, index) => {
                return (
                  <div key={index} style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem' 
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                      <div className="form-group">
                        <label>Part</label>
                        <select
                          className="form-control"
                          value={workOrderPart.partId}
                          onChange={(e) => updateWorkOrderPart(index, { partId: e.target.value })}
                        >
                          {parts.map(part => (
                            <option key={part.id} value={part.id}>
                              {part.name} ({part.sku})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Quantity</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={workOrderPart.quantity}
                          onChange={(e) => updateWorkOrderPart(index, { quantity: parseInt(e.target.value) })}
                        />
                      </div>

                      <div className="form-group">
                        <label>Time (minutes)</label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={workOrderPart.timeUsedMinutes}
                          onChange={(e) => updateWorkOrderPart(index, { timeUsedMinutes: parseInt(e.target.value) })}
                        />
                      </div>

                      <button 
                        type="button" 
                        onClick={() => removeWorkOrderPart(index)} 
                        className="btn btn-danger"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Work Order Activities */}
            {(formData.workOrderType === WorkOrderType.KITTING || formData.workOrderType === WorkOrderType.RECYCLING) && (
              <div style={{ marginTop: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4>Activities ({formData.activities?.length || 0})</h4>
                  <button type="button" onClick={addActivity} className="btn btn-secondary">
                    Add Activity
                  </button>
                </div>

                {formData.activities?.map((activity, index) => (
                  <div key={activity.id} style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    backgroundColor: activity.isCompleted ? '#f8f9fa' : 'white'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Activity description"
                        value={activity.description}
                        onChange={(e) => updateActivity(index, { description: e.target.value })}
                      />
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Est. minutes"
                        value={activity.estimatedMinutes || ''}
                        onChange={(e) => updateActivity(index, { estimatedMinutes: parseInt(e.target.value) || 0 })}
                        style={{ width: '120px' }}
                      />
                      <button 
                        type="button" 
                        onClick={() => removeActivity(index)} 
                        className="btn btn-danger"
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={activity.isCompleted}
                          onChange={(e) => updateActivity(index, { 
                            isCompleted: e.target.checked,
                            completedAt: e.target.checked ? new Date().toISOString() : undefined,
                            completedBy: e.target.checked ? formData.operator : undefined
                          })}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Completed
                      </label>
                      {activity.isCompleted && activity.completedAt && (
                        <small className="text-muted">
                          Completed {new Date(activity.completedAt).toLocaleString()}
                        </small>
                      )}
                    </div>
                    {activity.notes && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <small className="text-muted">Notes: {activity.notes}</small>
                      </div>
                    )}
                  </div>
                ))}

                {(!formData.activities || formData.activities.length === 0) && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#666', border: '1px dashed #ccc', borderRadius: '8px' }}>
                    No activities defined. Default activities are added automatically based on work order type.
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="form-control"
                rows={3}
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Work order notes, findings, quality inspection results, etc."
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingWorkOrder ? 'Update Work Order' : 'Create Work Order'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="form-section">
        <h3>Work Orders List ({workOrders.length} orders)</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Device S/N</th>
              <th>Category</th>
              <th>Work Order Type</th>
              <th>Location</th>
              <th>Operator</th>
              <th>Faults</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workOrders.map(workOrder => {
              const duration = workOrder.totalTimeMinutes 
                ? `${Math.floor(workOrder.totalTimeMinutes / 60)}h ${workOrder.totalTimeMinutes % 60}m`
                : 'In progress'

              return (
                <tr key={workOrder.id}>
                  <td><strong>{workOrder.deviceSerialNumber}</strong></td>
                  <td>{workOrder.deviceCategory}</td>
                  <td>{workOrder.workOrderType}</td>
                  <td>{workOrder.location}</td>
                  <td>{workOrder.operator}</td>
                  <td>
                    <div style={{ fontSize: '0.9rem' }}>
                      {workOrder.faults.map(fault => (
                        <div key={fault.id} style={{ marginBottom: '0.25rem' }}>
                          • {fault.description}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <select
                      className={`status-badge ${getStatusColor(workOrder.status)}`}
                      style={{ border: 'none', background: 'none' }}
                      value={workOrder.status}
                      onChange={(e) => handleStatusChange(workOrder.id, e.target.value as WorkOrderStatus)}
                    >
                      <option value={WorkOrderStatus.CREATED}>Created</option>
                      <option value={WorkOrderStatus.IN_PROGRESS}>In Progress</option>
                      <option value={WorkOrderStatus.AWAITING_PARTS}>Awaiting Parts</option>
                      <option value={WorkOrderStatus.QUALITY_INSPECTION}>Quality Inspection</option>
                      <option value={WorkOrderStatus.COMPLETED}>Completed</option>
                      <option value={WorkOrderStatus.CANCELLED}>Cancelled</option>
                    </select>
                  </td>
                  <td>{duration}</td>
                  <td>
                    <button 
                      onClick={() => handleEdit(workOrder)} 
                      className="btn btn-secondary"
                      style={{ marginRight: '0.5rem' }}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(workOrder.id)} 
                      className="btn btn-danger"
                      style={{ backgroundColor: '#dc3545', color: 'white' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {workOrders.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No work orders created yet. Click "Create Work Order" to get started.
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkOrders